import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { HaNPersonRelation } from "@lib/logic";
import { acceptConnection, sendConnection, useAppDispatch, withAsyncThunkErrorHandling } from "@lib/store";

import { PersonRelationIcons } from "./PersonRelationIcons";

type PersonConnectionActionProps = {
  userId: string;
  connectionId: string | null;
  relation: HaNPersonRelation;
};

// How the user relates to the person: a check once connected, a clock while their request waits, else a plus that
// connects, or that accepts the person's request
export const PersonConnectionAction = ({ userId, connectionId, relation }: PersonConnectionActionProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isSending, setIsSending] = useState(false);

  const connect = async () => {
    setIsSending(true);

    await dispatch(withAsyncThunkErrorHandling(() => sendConnection({ userId })));

    setIsSending(false);
  };

  const accept = async () => {
    if (!connectionId) return;

    setIsSending(true);

    await dispatch(withAsyncThunkErrorHandling(() => acceptConnection({ connectionId, userId })));

    setIsSending(false);
  };

  switch (relation) {
    case "connected":
      return (
        <PersonRelationIcons
          icon="check"
          label={t("connection:Connected")}
        />
      );

    case "requestSent":
      return (
        <PersonRelationIcons
          icon="clock"
          label={t("connection:ConnectionRequestSent")}
        />
      );

    case "requestReceived":
      return (
        <PersonRelationIcons
          icon="plus"
          label={t("connection:AcceptConnection")}
          isBusy={isSending}
          onClick={accept}
        />
      );

    case "none":
      return (
        <PersonRelationIcons
          icon="plus"
          label={t("connection:Connect")}
          isBusy={isSending}
          onClick={connect}
        />
      );
  }
};
